import Ionicons from "@expo/vector-icons/Ionicons";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { homeStrings } from "@/i18n/features/home.i18n";
import type { NearbyMode } from "@/state/session-store";

/**
 * Which list the People sheet is reading: the radius, or the space the viewer
 * is standing in.
 *
 * ## Why this is a mode and not a filter
 *
 * Every other control in this sheet narrows one list. This one swaps which
 * endpoint answers — `/presence/available` or `/presence/same-space` — and the
 * two ask genuinely different questions, so presenting it as another chip
 * alongside "Age range" would be a lie about what it does. It sits in place of
 * the range slider because the range is exactly what it replaces: in Same
 * Space, how far you can see stops deciding who you see.
 *
 * The slider is HIDDEN rather than disabled in Same Space, and that is a real
 * choice. `nearbyRadiusM` is one saved setting that also scopes the GROUPS
 * query, so greying it out here would say "your range is off" about a value
 * that is still very much on somewhere else. Hiding it from the sheet that no
 * longer obeys it says the true thing, and the setting stays reachable from
 * the Groups sheet and Edit profile.
 *
 * ## When it does not appear at all
 *
 * Only when `available` is true, which the caller derives from the venue being
 * a real place rather than an anonymous grid cell. The server's Same Space
 * query joins on `grid_key IS NULL`, so offering the mode on a cell would
 * return an empty list every time with nothing on screen to explain it — and
 * `venue.named` cannot stand in for the check, because a cell that has been
 * reverse-geocoded wears a real name while still being refused.
 */
export function NearbyModeToggle({
  mode,
  joinedSpaceName,
  onSelectSameSpace,
  onChangeSpace,
  onSelectNearby,
  colors,
}: {
  mode: NearbyMode;
  /**
   * The space actually joined, once participation exists. Null while in the
   * radius list — there is deliberately nothing to show before the user has
   * named a place, because the server's guess is not the answer.
   */
  joinedSpaceName: string | null;
  /**
   * Chooses Same Space. Opens Join Space when there is no participation yet —
   * that is the opt-in — and otherwise just shows the list it already has.
   */
  onSelectSameSpace: () => void;
  /**
   * Opens Join Space to pick a DIFFERENT one, while already participating.
   *
   * Distinct from `onSelectSameSpace`, and the two only look alike. Choosing
   * the option when a participation already exists just shows its list —
   * asking for a consent the server already holds would be noise. This is the
   * separate act of changing which space that is, and wiring the link to the
   * option made it dead: the label said "Change space" and it reloaded the
   * same one.
   */
  onChangeSpace: () => void;
  /** Leaves participation and returns to the radius list. */
  onSelectNearby: () => void;
  colors: {
    accent: string;
    accentSoft: string;
    border: string;
    label: string;
    labelMuted: string;
    surface: string;
  };
}) {
  const { t } = useTranslation();
  const sameSpace = mode === "same_space";

  const option = (value: NearbyMode, label: string) => {
    const selected = mode === value;
    const choose = value === "same_space" ? onSelectSameSpace : onSelectNearby;
    return (
      <Pressable
        key={value}
        accessibilityRole="radio"
        accessibilityState={{ selected }}
        accessibilityLabel={label}
        // Selecting a mode never asks for consent that already exists.
        // `onSelectSameSpace` opens Join Space only when there is no
        // participation yet — that IS the opt-in — and otherwise just shows
        // the list the server already holds. Correcting which space it is
        // lives on its own action below, because a picker that reloaded the
        // same space was a link that read "Change space" and changed nothing.
        onPress={choose}
        style={[
          styles.option,
          { borderColor: selected ? colors.accent : colors.border },
          selected && { backgroundColor: colors.accentSoft },
        ]}
      >
        <Text
          numberOfLines={1}
          style={[
            styles.optionText,
            { color: selected ? colors.accent : colors.labelMuted },
          ]}
        >
          {label}
        </Text>
      </Pressable>
    );
  };

  return (
    <View style={styles.root}>
      <View accessibilityRole="radiogroup" style={styles.options}>
        {option("radius", t(homeStrings.modeRadius))}
        {option("same_space", t(homeStrings.modeSameSpace))}
      </View>

      {/* The hint for the ACTIVE mode only. Both at once reads as a comparison
          table for a two-item choice, and the inactive half is noise.

          There were three, and the third has been deleted rather than left to
          rot: "Same Space is on for X, choose Nearby to leave" existed for a
          state that can no longer happen. It described somebody reading the
          radius list while still joined to a space, which was possible only
          while the mode was stored beside the participation and could
          disagree with it. The mode is derived from the participation now, so
          a joined space always shows Same Space selected — the sentence would
          have sat under its own contradiction. */}
      <Text style={[styles.hint, { color: colors.labelMuted }]}>
        {sameSpace && joinedSpaceName
          ? t(homeStrings.modeSameSpaceHint, { space: joinedSpaceName })
          : t(homeStrings.modeRadiusHint)}
      </Text>

      {/* The space is named by the USER, through Join Space, so this line is
          how they change it — and it is the same screen the header card opens.
          There is no "confirm the space we guessed", because the guess is
          routinely the business next door and confirming it would be the
          inferred model wearing an opt-in. */}
      {sameSpace ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(homeStrings.sameSpaceJoined, {
            space: joinedSpaceName ?? "",
          })}
          onPress={onChangeSpace}
          style={({ pressed }) => [
            styles.change,
            { borderColor: colors.border, backgroundColor: colors.surface },
            pressed && { opacity: 0.7 },
          ]}
        >
          <Ionicons name="location-outline" size={15} color={colors.accent} />
          <Text style={[styles.changeText, { color: colors.accent }]}>
            {t(homeStrings.sameSpaceJoin)}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { gap: 8 },
  options: { flexDirection: "row", gap: 8 },
  option: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 9,
    paddingHorizontal: 10,
    alignItems: "center",
  },
  optionText: { fontSize: 13, fontWeight: "600" },
  hint: { fontSize: 12, lineHeight: 16 },
  change: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 9,
  },
  changeText: { fontSize: 13, fontWeight: "600" },
});
